/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Grid, TextField, Tabs, Tab, Box } from "@mui/material";
import { TextValidator } from "react-material-ui-form-validator";
import { DeviceModel, LocationModel } from "../../Utils/Models";
import { handleChangeAutocomplete, onChangeTextField } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useEffect, useState } from "react";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";
import { toast } from "react-toastify";

type LocationProps = {
  location: LocationModel;
  deliveries: any;
  handleChangeLocation: (product: LocationModel) => void;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function Location(props: LocationProps) {
  const { location, deliveries, handleChangeLocation } = props;
  const [tabValue, setTabValue] = useState(0);
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<DeviceModel[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (location._id && tabValue === 1) {
      fetchDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location._id, tabValue]);

  useEffect(() => {
    filterDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, devices]);

  /**
   * Fetch devices for this location
   */
  async function fetchDevices() {
    try {
      const response = await getData(`${urlEnum.devices}/location/${location._id}`);
      if (response && response.data && response.data.result) {
        setDevices(response.data.result);
        setFilteredDevices(response.data.result);
      }
    } catch (error) {
      toast.error(`Failed to fetch devices: ${error}`);
    }
  }

  /**
   * Filter devices based on search term
   */
  function filterDevices() {
    if (!searchTerm) {
      setFilteredDevices(devices);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = devices.filter(
      (device) =>
        device.name?.toLowerCase().includes(term) ||
        device.serialNumber?.toLowerCase().includes(term) ||
        device.brand?.name?.toLowerCase().includes(term) ||
        device.model?.name?.toLowerCase().includes(term) ||
        device.code?.toLowerCase().includes(term)
    );
    setFilteredDevices(filtered);
  }

  /**
   * Handle tab change
   */
  function handleTabChange(event: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue);
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="location tabs"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={Vocabulary.generalInformation || "General Information"} />
        <Tab label={Vocabulary.devices || "Devices"} disabled={!location._id} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="name"
              id="name"
              label={Vocabulary.name}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={location?.name}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="email"
              id="email"
              label={Vocabulary.email}
              fullWidth
              errorMessages={[Vocabulary.requiredField]}
              value={location?.email}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="phone"
              id="phone"
              label={Vocabulary.phone}
              fullWidth
              errorMessages={[Vocabulary.requiredField]}
              value={location?.phone}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="country"
              id="country"
              label={Vocabulary.country}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={location?.country}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="address"
              id="address"
              label={Vocabulary.address}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={location?.address}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="zip"
              id="zip"
              label={Vocabulary.zip}
              fullWidth
              value={location?.zip}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="state"
              id="state"
              label={Vocabulary.state}
              fullWidth
              value={location?.state}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="city"
              id="city"
              label={Vocabulary.city}
              fullWidth
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              value={location?.city}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="longitude"
              id="longitude"
              label={Vocabulary.longitude}
              fullWidth
              value={location?.longitude}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              variant="outlined"
              name="latitude"
              id="latitude"
              label={Vocabulary.latitude}
              fullWidth
              value={location?.latitude}
              onChange={(event) =>
                handleChangeLocation(onChangeTextField(event, location))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Autocomplete
              size="small"
              id={"delivers"}
              multiple
              freeSolo={false}
              disablePortal
              options={deliveries || []}
              value={location?.deliveries || []}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option: any, value: any) =>
                option.name === value.name
              }
              getOptionDisabled={(option: any) =>
                !!(
                  location?.deliveries &&
                  location?.deliveries.find(
                    (element: any) => element?.name === option?.name
                  )
                )
              }
              onChange={(event: any, value: any) =>
                handleChangeLocation(
                  handleChangeAutocomplete(event, value, location, "deliveries")
                )
              }
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label={Vocabulary.deliveries}
                  variant="outlined"
                />
              )}
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              size="small"
              variant="outlined"
              label={Vocabulary.search || "Search"}
              placeholder="Search by name, serial number, brand, model, or code"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {filteredDevices.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                {devices.length === 0
                  ? Vocabulary.noDevicesFound || "No devices found for this location"
                  : Vocabulary.noResultsFound || "No devices match your search"}
              </Box>
            ) : (
              <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                <Grid container spacing={2}>
                  {filteredDevices.map((device, index) => (
                    <Grid item xs={12} key={device._id || index}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6} md={4}>
                            <strong>{Vocabulary.name}:</strong> {device.name}
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <strong>{Vocabulary.serialNumber}:</strong>{" "}
                            {device.serialNumber}
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <strong>{Vocabulary.code}:</strong> {device.code}
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <strong>{Vocabulary.brand}:</strong> {device.brand?.name}
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <strong>{Vocabulary.model}:</strong> {device.model?.name}
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
}
